import React, { useCallback, useMemo, useState, useEffect } from 'react';

import styled, { css, keyframes } from 'styled-components';
import { deleteGallery } from '@store/firebase/gallery/delete-gallery';
import Tooltip from '@components/dataDisplay/Tooltip';
import { IgalleryItem } from '@typings/pages/gallery.interface';
import { timestampToDateString } from '@utils/dateAgo';
import { ACCESS_TYPE } from '@typings/common/accessType';
import SecretItem from '@template/PermissionItem/SecretItem';
import { useReadPermission } from '@hooks/usePermission';
import IconButton from '@components/dataEntry/button/IconButton';
import LockIcon from '@template/GuardItem/LockIcon';
import {
  PAGE_LAYOUT_TYPE,
  PAGE_LAYOUT_TYPE_VALUE,
} from '@typings/pages/pageLayout.interface';
import Icon from '@components/dataDisplay/Icons/Icon';

const BoxHover = css`
  .albumImage {
    transform: scale(1.05);
    position: relative;
    cursor: var(--hoverCursorURL), pointer;
    opacity: 1;
  }
`;

const ButtonWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;

  position: absolute;
  right: 12px;
  top: 12px;
`;

const InfoSec = styled.div`
  padding: 4px 0;
  display: grid;
  align-items: center;
  gap: 4px;
  grid-template-columns: auto auto 1fr;
`;

const Date = styled.span`
  font-weight: 700;
  font-size: 12px;
  width: -webkit-fill-available;
`;

const Title = styled.span`
  font-weight: 300;
  font-size: 12px;
  width: 100%;
  text-align: right;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  font-size: 11px;
  color: #fff;
  line-height: 1.2;
  font-weight: 700;
  position: absolute;
  bottom: 4px;
  right: 4px;
`;

const layoutMap = {
  [PAGE_LAYOUT_TYPE.MASONRY]: css`
    height: auto;
    border-radius: var(--cardBorderRadius);
  `,
  [PAGE_LAYOUT_TYPE.DEFAULT]: css`
    height: ${({ contentHeight }: { contentHeight?: string }) =>
      contentHeight || '300px'};
  `,
};

const AlbumCardMask = styled.div<{
  isEdit?: boolean;
  docLayout: PAGE_LAYOUT_TYPE_VALUE;
  contentHeight?: string;
}>`
  position: relative;
  margin: 4px;
  width: 100%;
  ${({ docLayout }) => layoutMap[docLayout]};
  overflow: hidden;
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2);
  z-index: 0;
  backdrop-filter: blur(12px);

  &:hover {
    cursor: var(--hoverCursorURL), pointer;
    ${({ isEdit }) => !isEdit && BoxHover};
  }
`;

// 스켈레톤 애니메이션 (세로)
const shimmer = keyframes`
  0% {
    background-position: 0 -200%;
  }
  100% {
    background-position: 0 200%;
  }
`;

const SkeletonImage = styled.div<{
  isMasonry: boolean;
  contentHeight?: string;
}>`
  width: 100%;
  height: ${({ isMasonry, contentHeight }) => (isMasonry ? '200px' : contentHeight || '100%')};
  background: linear-gradient(
    180deg,
    var(--cardBgColor, #f0f0f0) 25%,
    var(--lineColor, #e0e0e0) 50%,
    var(--cardBgColor, #f0f0f0) 75%
  );
  background-size: 100% 200%;
  animation: ${shimmer} 1.5s infinite ease-in-out;
`;

const AlbumImage = styled.img<{
  isMasonry: boolean;
  isLoaded: boolean;
}>`
  width: 100%;
  height: ${({ isMasonry }) => (isMasonry ? 'auto' : '100%')};
  object-fit: cover;
  display: ${({ isLoaded }) => (isLoaded ? 'block' : 'none')};
  transition: transform ease-in-out 0.2s;
`;

const Overlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  color: #fff;
  font-size: 12px;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.65) 100%
  );
  transform: translateY(100%);
  opacity: 0;
  transition: all 0.2s ease-in-out;

  ${AlbumCardMask}:hover & {
    transform: translateY(0);
    opacity: 1;
  }
`;

type Props = {
  list: IgalleryItem;
  isEdit?: boolean;
  collectionPath: string;
  contentHeight: string;
  docLayout: PAGE_LAYOUT_TYPE_VALUE;
};

function GalleryItem({
  list,
  isEdit,
  collectionPath,
  contentHeight,
  docLayout,
}: Props) {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  
  const {
    imgURL,
    imgURLList,
    title,
    date,
    id: docId,
    objectID,
    accessType,
    authorUid,
  } = list;

  const imageSource = imgURL || imgURLList?.[0];

  // 이미지 URL이 변경되면 로딩 상태 리셋
  useEffect(() => {
    setIsImageLoaded(false);
  }, [imageSource]);

  const customDate = timestampToDateString(date, 'YYYY-MM-DD');

  const handleDelete = useCallback(
    async (e) => {
      e.stopPropagation();
      if (confirm('갤러리를 삭제하시겠습니까?')) {
        await deleteGallery({
          docId: docId || objectID || '',
          imgURL: list ? list.imgURL || '' : '',
          hashtagIdList: list?.hashtagIdList,
          collectionPath,
        });
        if (close) close();
      } else {
        return;
      }
    },
    [imgURL, collectionPath, list],
  );

  const isMasonry = docLayout === PAGE_LAYOUT_TYPE.MASONRY;
  const imageCount = (imgURLList && imgURLList.length) || (imgURL ? 1 : 0);

  const readPermission = useReadPermission({
    accessType: accessType || ACCESS_TYPE.PUBLIC,
    authorUid: authorUid || '',
  });

  const countBadge = useMemo(() => {
    if (imageCount > 1) {
      return (
        <CountBadge>
          <Icon name="Block14" color="#fff" />
          {imageCount}
        </CountBadge>
      );
    }
    return null;
  }, [imageCount]);

  if (!readPermission)
    return (
      <SecretItem
        accessType={accessType}
        style={{ height: '100%', justifyContent: 'center' }}
      />
    );

  return (
    <>
      <AlbumCardMask
        isEdit={isEdit}
        docLayout={docLayout}
        contentHeight={contentHeight}
      >
        {!isImageLoaded && (
          <SkeletonImage isMasonry={isMasonry} contentHeight={contentHeight} />
        )}
        <AlbumImage
          className="albumImage"
          src={imageSource}
          alt={title || 'gallery image'}
          isMasonry={isMasonry}
          isLoaded={isImageLoaded}
          onLoad={() => setIsImageLoaded(true)}
        />
        {countBadge}
        {isMasonry && (
          <Overlay>
            <Date>{customDate}</Date>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <LockIcon accessType={accessType} />
              <Title>{title}</Title>
            </div>
          </Overlay>
        )}

        {isEdit && (
          <ButtonWrap>
            <Tooltip message="삭제" direction="bottom">
              <IconButton onClick={handleDelete} icon="Trash14" />
            </Tooltip>
          </ButtonWrap>
        )}
      </AlbumCardMask>

      {!isMasonry && (
        <InfoSec>
          <Date>{customDate}</Date>
          <LockIcon accessType={accessType} />
          <Title>{title}</Title>
        </InfoSec>
      )}
    </>
  );
}

export default GalleryItem;
